/**
 * UI-3b Task 4 — ContactsPage.
 *
 * Tenant-admin customer contacts list. Composition is plan §0.C (authoritative):
 *
 *   - The list is fetched with a LOCAL `useEffect + api.get` — NOT `useAdminResource`.
 *     The query-string `?q=` filter breaks the hook's `update(id)`/`remove(id)`
 *     URL building, so mutations (create/edit/delete) are wired manually.
 *   - Search is debounced 300ms (`useDebouncedValue`); the debounced value is the
 *     `load` dependency so a refetch fires once typing settles.
 *   - Create / Edit go through `ContactDrawer` (the drawer owns the save API call
 *     and reports back via `onSaved`).
 *   - Delete opens a `confirmDialog`; accepting issues `DELETE /contacts/:id`.
 *   - Two-tier write gate: the page is visible to `calls:view`, but write
 *     affordances (+ New Contact, row Edit/Delete) require `agents:manage`.
 *
 * Backend contract (apps/api/src/routes/contacts/list.ts):
 *   GET /contacts?q=        -> { contacts, total, limit, offset }
 *
 * Pagination: §0.C does not implement pagination controls — the backend's
 * default limit/offset returns the first page; search narrows results. Phase-1
 * ContactsPage ships without explicit Next/Prev controls per §0.C.
 */
import { useCallback, useEffect, useRef, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog'
import { Tag } from 'primereact/tag'

import { api } from '../../api/client.js'
import { useAuthStore } from '../../store/authStore.js'
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import PageHeader from '../../components/admin/PageHeader.js'
import ResourceTable, { type ColumnDef } from '../../components/admin/ResourceTable.js'
import { ContactDrawer, type Contact } from '../../components/admin/contacts/ContactDrawer.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { timestampMs } from '../../utils/timezone.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * List-row shape: the `Contact` entity exported by `ContactDrawer` (the single
 * source of truth for the shared fields) extended with the two list-only
 * computed columns the `GET /contacts` endpoint returns.
 */
interface ContactRow extends Contact {
  lastCallAt: string | null
  totalCalls: number
}

interface ListResponse {
  contacts: ContactRow[]
  total: number
  limit: number
  offset: number
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Coarse relative-time label; em-dash for a null timestamp. */
function formatRelative(iso: string | null): string {
  if (!iso) return '—'
  const t = timestampMs(iso)
  const diff = Math.max(0, Date.now() - t)
  const m = Math.floor(diff / 60_000)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

function truncate(s: string | null, n: number): string {
  if (!s) return ''
  return s.length > n ? s.slice(0, n) + '…' : s
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ContactsPage(): JSX.Element {
  const permissions = useAuthStore((s) => s.user?.permissions ?? [])
  const canWrite = permissions.includes('agents:manage')
  const toast = useToast()

  const [contacts, setContacts] = useState<ContactRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const debouncedQ = useDebouncedValue(q, 300)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<ContactRow | null>(null)

  // True while the component is mounted. The local-fetch list (§0.C — no
  // `useAdminResource`) resolves asynchronously; this guard suppresses stale
  // state writes from a fetch that completes after unmount.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const qs = debouncedQ ? `?q=${encodeURIComponent(debouncedQ)}` : ''
      const resp = await api.get<ListResponse>(`/contacts${qs}`)
      // Apply data + clear the loading flag in one continuation so React 18
      // batches them into a single re-render (no trailing `setLoading` tick).
      if (mounted.current) {
        setContacts(resp.contacts)
        setLoading(false)
      }
    } catch (e) {
      if (mounted.current) {
        setError(e instanceof Error ? e.message : 'Failed to load contacts.')
        setLoading(false)
      }
    }
  }, [debouncedQ])

  useEffect(() => {
    void load()
  }, [load])

  function handleDelete(c: ContactRow): void {
    confirmDialog({
      message: `Permanently delete "${c.name}" (${c.phoneE164})? Linked conversations remain.`,
      header: 'Delete Contact',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      // The whole body is try/catch-wrapped so a rejecting `api.delete` can
      // never escape as an unhandled rejection — on failure the user gets a
      // readable error toast instead of silent nothing.
      accept: async () => {
        try {
          await api.delete(`/contacts/${c.id}`)
          await load()
        } catch (e) {
          toast.error(e instanceof Error ? e.message : 'Failed to delete contact')
        }
      },
    })
  }

  const columns: ColumnDef<ContactRow>[] = [
    { label: 'Name', accessor: 'name' },
    { label: 'Phone', accessor: 'phoneE164' },
    {
      label: 'Consent',
      accessor: (r) => {
        const s = r.consentStatus ?? 'PENDING'
        return (
          <Tag
            value={s}
            severity={s === 'GRANTED' ? 'success' : s === 'REVOKED' ? 'danger' : 'warning'}
          />
        )
      },
    },
    { label: 'Email', accessor: (r) => r.email ?? '—' },
    { label: 'Last Call', accessor: (r) => formatRelative(r.lastCallAt) },
    { label: 'Total Calls', accessor: 'totalCalls' },
    { label: 'Notes', accessor: (r) => truncate(r.notes, 50) },
    { label: 'Updated', accessor: (r) => formatRelative(r.updatedAt) },
  ]

  // Search-specific empty state (spec §3.6): a non-empty query that matched
  // nothing gets a tailored message; an empty list with no query is generic.
  const emptyTitle = debouncedQ
    ? `No contacts match "${debouncedQ}"`
    : 'No contacts'
  const emptyDescription = debouncedQ
    ? 'Try a different name, phone number, or email.'
    : 'Tenant customer contacts will appear here.'

  return (
    <RequirePermission permission="admin:calls:view">
      <div className="space-y-4 p-4">
        <PageHeader
          title="Contacts"
          description="Tenant customer contacts."
          actions={
            canWrite ? (
              <Button
                label="+ New Contact"
                onClick={() => {
                  setEditing(null)
                  setDrawerOpen(true)
                }}
              />
            ) : null
          }
        />

        {/* Backend ILIKE search covers name + phoneE164 only; email search is a
            deferred backend follow-up (UI-3b scope: frontend copy only). */}
        <input
          aria-label="Search contacts"
          placeholder="Search by name / phone"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full rounded border px-3 py-2"
        />

        {error && (
          <div
            role="alert"
            className="rounded bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {error}{' '}
            <button onClick={() => void load()} className="underline">
              Retry
            </button>
          </div>
        )}

        <p aria-live="polite" className="sr-only">
          {loading ? 'Loading contacts' : `${contacts.length} contacts`}
        </p>

        <ResourceTable<ContactRow>
          columns={columns}
          rows={contacts}
          loading={loading}
          actions={canWrite ? ['edit', 'delete'] : []}
          onEdit={(c) => {
            setEditing(c)
            setDrawerOpen(true)
          }}
          onDelete={handleDelete}
          empty={{
            title: emptyTitle,
            description: emptyDescription,
            icon: 'pi pi-users',
          }}
        />

        <ContactDrawer
          open={drawerOpen}
          contact={editing}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            void load()
            setDrawerOpen(false)
          }}
        />

        <ConfirmDialog />
      </div>
    </RequirePermission>
  )
}
